/**
 * Calculator Slice (Redux Toolkit)
 *
 * Client-only / ephemeral calculator inputs + UI state. Replaces sauron's
 * `CalculatorProvider` React Context. Server data (lookup / workspace /
 * marketplaces) lives in `calculatorApi` (RTK Query); derived metrics are
 * computed client-side by the @domain engines off this slice's inputs.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { STEADY_STATE_TACOS_PERCENT } from '@/domain/profitability/types';
import { addScenario, closeScenario } from '@/domain/advanced_economics/engine';
import type { RootState } from '../types';
import type { FeeBasis } from '@/domain/profitability/types';
import type {
    CogsFieldState,
    CogsCompoundFieldState,
} from '@/domain/cogs/types';
import type { ScenarioInput, TemplateId } from '@/domain/advanced_economics/types';

export type CalculatorBusinessModel = 'Private Label' | 'Wholesale';
export type AppStatus = 'empty' | 'loading' | 'loaded';
export type CalculatorTab = 0 | 1 | 2 | 3; // 0 Profitability · 1 COGS · 2 Advanced · 3 Launch Budget

const DEFAULT_SELL_PRICE = 24.99;

export const toCanonicalBM = (bm: CalculatorBusinessModel): 'private_label' | 'wholesale' =>
    bm === 'Private Label' ? 'private_label' : 'wholesale';

function defaultFeeBasis(): FeeBasis {
    return {
        referralFeeRate: 0.15,
        fbaFixedFee: 3.22,
        returnsRate: 0.04,
        returnsRecoveryRate: 0.4,
        referralFeeKnown: true,
        fbaFeeKnown: false,
        returnsRateKnown: false,
        dimensionsKnown: false,
        feesDerivedFromDimensions: false,
    };
}

function baseScenario(): ScenarioInput {
    return {
        slotId: 's1',
        templateId: 'steady_state',
        tacosPercent: null,
        tacosCustom: false,
        sellPrice: DEFAULT_SELL_PRICE,
        referralFeeRate: 0.15,
        fbaFixedFee: 3.22,
        returnsRate: 0.04,
        returnsRecoveryRate: 0.4,
        cogs: null,
        otherExpenses: null,
        otherExpensesMode: 'per_unit',
        advertisingEnabled: true,
        unitsSoldOnThisScenario: null,
        compareAgainstLeft: false,
    };
}

function defaultCompound(): CogsCompoundFieldState {
    return {
        amountValue: null,
        amountEntryMode: 'unit',
        amountBatchTotal: null,
        amountBatchSize: null,
        amountSourceGroup: 'manual',
        percentageValue: null,
        percentageSourceGroup: 'manual',
    };
}

export interface CalculatorState {
    // Navigation
    activeTab: CalculatorTab;

    // Product identity / marketplace
    asin: string | null;
    marketplace: string;

    // Product display (from lookup — shown in the context bar)
    productName: string | null;
    imageUrl: string | null;
    fulfillmentType: string | null;
    bsrDisplay: string | null;
    category: string | null;

    // Product context inputs
    sellPrice: number;
    sellPriceIsCustom: boolean;
    businessModel: CalculatorBusinessModel;

    // Fee basis (Advanced Economics editable / Result Breakdown overrides)
    feeBasis: FeeBasis;
    feeUserEdited: { referralFee: boolean; fbaFee: boolean; returnsRate: boolean };

    // Profitability Check inputs
    cogsInput: string;
    estimatedCogs: number | null;
    advertisingEnabled: boolean;
    tacosPercent: number;
    tacosIsCustom: boolean;

    // COGS Builder
    cogsFields: CogsFieldState[];
    cogsCompound: CogsCompoundFieldState;
    cogsUiMode: 'simple' | 'detailed';

    // Advanced Economics
    aeScenarios: ScenarioInput[];

    // Lifecycle
    appStatus: AppStatus;
}

const initialState: CalculatorState = {
    activeTab: 0,
    asin: null,
    marketplace: 'US',
    productName: null,
    imageUrl: null,
    fulfillmentType: null,
    bsrDisplay: null,
    category: null,
    sellPrice: DEFAULT_SELL_PRICE,
    sellPriceIsCustom: false,
    businessModel: 'Private Label',
    feeBasis: defaultFeeBasis(),
    feeUserEdited: { referralFee: false, fbaFee: false, returnsRate: false },
    cogsInput: '',
    estimatedCogs: null,
    advertisingEnabled: false,
    tacosPercent: STEADY_STATE_TACOS_PERCENT,
    tacosIsCustom: false,
    cogsFields: [],
    cogsCompound: defaultCompound(),
    cogsUiMode: 'simple',
    aeScenarios: [baseScenario()],
    appStatus: 'loading',
};

/** Product context + fee basis payload (camelCase) used to hydrate the slice after a lookup. */
export interface LoadedProductPayload {
    asin: string;
    marketplace: string;
    productName: string | null;
    imageUrl: string | null;
    fulfillmentType: string | null;
    bsrDisplay: string | null;
    category: string | null;
    sellPrice: number;
    estimatedCogs: number | null;
    estimatedMonthlyUnitsSold: number | null;
    feeBasis: FeeBasis;
}

const calculatorSlice = createSlice({
    name: 'calculator',
    initialState,
    reducers: {
        setActiveTab(state, action: PayloadAction<CalculatorTab>) {
            state.activeTab = action.payload;
        },
        setMarketplace(state, action: PayloadAction<string>) {
            state.marketplace = action.payload;
        },
        setAppStatus(state, action: PayloadAction<AppStatus>) {
            state.appStatus = action.payload;
        },
        setSellPrice(state, action: PayloadAction<number>) {
            state.sellPrice = action.payload;
            state.sellPriceIsCustom = true;
            state.aeScenarios.forEach(s => {
                s.sellPrice = action.payload;
            });
        },
        setBusinessModel(state, action: PayloadAction<CalculatorBusinessModel>) {
            state.businessModel = action.payload;
        },
        setFeeBasis(state, action: PayloadAction<Partial<FeeBasis>>) {
            state.feeBasis = { ...state.feeBasis, ...action.payload };
            const { referralFeeRate, fbaFixedFee, returnsRate, returnsRecoveryRate } = action.payload;
            state.aeScenarios.forEach(s => {
                if (referralFeeRate !== undefined) s.referralFeeRate = referralFeeRate;
                if (fbaFixedFee !== undefined) s.fbaFixedFee = fbaFixedFee;
                if (returnsRate !== undefined) s.returnsRate = returnsRate;
                if (returnsRecoveryRate !== undefined) s.returnsRecoveryRate = returnsRecoveryRate;
            });
        },
        overrideFee(
            state,
            action: PayloadAction<{
                field: 'referralFeeRate' | 'fbaFixedFee' | 'returnsRate';
                value: number;
            }>
        ) {
            const { field, value } = action.payload;
            state.feeBasis[field] = value;
            state.aeScenarios.forEach(s => {
                s[field] = value;
            });
            const key =
                field === 'referralFeeRate'
                    ? 'referralFee'
                    : field === 'fbaFixedFee'
                      ? 'fbaFee'
                      : 'returnsRate';
            state.feeUserEdited[key] = true;
        },
        setCogsInput(state, action: PayloadAction<string>) {
            state.cogsInput = action.payload;
        },
        setAdvertisingEnabled(state, action: PayloadAction<boolean>) {
            state.advertisingEnabled = action.payload;
        },
        setTacosPercent(state, action: PayloadAction<number>) {
            const clamped = Math.min(100, Math.max(0, action.payload));
            state.tacosPercent = clamped;
            if (clamped !== STEADY_STATE_TACOS_PERCENT) state.tacosIsCustom = true;
        },
        // Apply a COGS value to Profitability Check (+ AE baseline scenario)
        applyCogsValue(state, action: PayloadAction<number>) {
            state.cogsInput = action.payload.toFixed(2);
            if (state.aeScenarios[0]) state.aeScenarios[0].cogs = action.payload;
        },
        setCogsFields(state, action: PayloadAction<CogsFieldState[]>) {
            state.cogsFields = action.payload;
        },
        setCogsCompound(state, action: PayloadAction<Partial<CogsCompoundFieldState>>) {
            state.cogsCompound = { ...state.cogsCompound, ...action.payload };
        },
        setCogsUiMode(state, action: PayloadAction<'simple' | 'detailed'>) {
            state.cogsUiMode = action.payload;
        },
        // Advanced Economics scenario management (delegates to engine helpers)
        addAEScenario(state, action: PayloadAction<TemplateId | undefined>) {
            const { scenarios } = addScenario(state.aeScenarios, action.payload ?? 'stabilizing');
            state.aeScenarios = scenarios;
        },
        closeAEScenario(state, action: PayloadAction<string>) {
            const { scenarios } = closeScenario(state.aeScenarios, action.payload);
            state.aeScenarios = scenarios;
        },
        updateAEScenario(
            state,
            action: PayloadAction<{ slotId: string; updates: Partial<ScenarioInput> }>
        ) {
            const { slotId, updates } = action.payload;
            const s = state.aeScenarios.find(x => x.slotId === slotId);
            if (s) Object.assign(s, updates);
        },
        // Hydrate the slice from a product lookup result (overlay product defaults)
        loadProduct(state, action: PayloadAction<LoadedProductPayload>) {
            const p = action.payload;
            state.asin = p.asin;
            state.marketplace = p.marketplace;
            state.productName = p.productName;
            state.imageUrl = p.imageUrl;
            state.fulfillmentType = p.fulfillmentType;
            state.bsrDisplay = p.bsrDisplay;
            state.category = p.category;
            state.sellPrice = p.sellPrice || DEFAULT_SELL_PRICE;
            state.estimatedCogs = p.estimatedCogs;
            state.feeBasis = p.feeBasis;
            state.sellPriceIsCustom = false;
            state.tacosIsCustom = false;
            state.feeUserEdited = { referralFee: false, fbaFee: false, returnsRate: false };
            state.aeScenarios.forEach(s => {
                s.sellPrice = state.sellPrice;
                s.cogs = p.estimatedCogs ?? s.cogs;
                s.unitsSoldOnThisScenario = p.estimatedMonthlyUnitsSold ?? s.unitsSoldOnThisScenario;
                s.referralFeeRate = p.feeBasis.referralFeeRate;
                s.fbaFixedFee = p.feeBasis.fbaFixedFee;
                s.returnsRate = p.feeBasis.returnsRate;
                s.returnsRecoveryRate = p.feeBasis.returnsRecoveryRate;
            });
            state.appStatus = 'loaded';
        },
        // Overlay a saved workspace record (snake_case from DB) onto current state.
        // `restoreActiveTab: false` keeps the current tab — used when the tab came
        // from an explicit `?tab=` deep link that must win over the saved one.
        applyWorkspaceRecord(
            state,
            action: PayloadAction<{
                record: Record<string, unknown>;
                restoreActiveTab?: boolean;
            }>
        ) {
            const { record: ws, restoreActiveTab = true } = action.payload;

            const pc = ws.profitability_check_state as Record<string, unknown> | undefined;
            if (pc) {
                if (typeof pc.sell_price === 'number' && pc.sell_price > 0) {
                    state.sellPrice = pc.sell_price;
                    state.sellPriceIsCustom = true;
                }
                if (typeof pc.cogs_value === 'number') state.cogsInput = pc.cogs_value.toFixed(2);
                if (typeof pc.advertising_enabled === 'boolean')
                    state.advertisingEnabled = pc.advertising_enabled;
                if (typeof pc.tacos_percent === 'number') state.tacosPercent = pc.tacos_percent;
                if (typeof pc.referral_fee_rate === 'number')
                    state.feeBasis.referralFeeRate = pc.referral_fee_rate;
                if (typeof pc.fba_fixed_fee === 'number')
                    state.feeBasis.fbaFixedFee = pc.fba_fixed_fee;
                if (typeof pc.returns_rate === 'number') state.feeBasis.returnsRate = pc.returns_rate;
            }

            const cb = ws.cogs_builder_state as Record<string, unknown> | undefined;
            if (cb) {
                if (cb.ui_mode === 'simple' || cb.ui_mode === 'detailed') state.cogsUiMode = cb.ui_mode;
                if (Array.isArray(cb.fields)) {
                    state.cogsFields = (cb.fields as Array<Record<string, unknown>>).map(f => ({
                        fieldId: (f.field_id ?? f.fieldId) as string,
                        value: (f.value as number | null) ?? null,
                        entryMode: ((f.entry_mode ?? f.entryMode ?? 'unit') as string) as
                            | 'unit'
                            | 'batch',
                        batchTotalAmount: (f.batch_total_amount ?? f.batchTotalAmount ?? null) as
                            | number
                            | null,
                        batchSize: (f.batch_size ?? f.batchSize ?? null) as number | null,
                        sourceGroup: ((f.source_group ?? f.sourceGroup ?? 'manual') as string) as
                            | 'estimated'
                            | 'manual'
                            | 'saved_draft',
                        sourceDetail: (f.source_detail ?? f.sourceDetail ?? null) as never,
                        visible: typeof f.visible === 'boolean' ? f.visible : true,
                    }));
                }
                if (cb.compound_fields && typeof cb.compound_fields === 'object') {
                    const cf = cb.compound_fields as Record<string, unknown>;
                    state.cogsCompound = {
                        amountValue: ((cf.amount_value ?? cf.amountValue) ??
                            state.cogsCompound.amountValue) as number | null,
                        amountEntryMode: ((cf.amount_entry_mode ??
                            cf.amountEntryMode ??
                            state.cogsCompound.amountEntryMode) as string) as 'unit' | 'batch',
                        amountBatchTotal: ((cf.amount_batch_total ?? cf.amountBatchTotal) ??
                            state.cogsCompound.amountBatchTotal) as number | null,
                        amountBatchSize: ((cf.amount_batch_size ?? cf.amountBatchSize) ??
                            state.cogsCompound.amountBatchSize) as number | null,
                        amountSourceGroup: ((cf.amount_source_group ??
                            cf.amountSourceGroup ??
                            state.cogsCompound.amountSourceGroup) as string) as never,
                        percentageValue: ((cf.percentage_value ?? cf.percentageValue) ??
                            state.cogsCompound.percentageValue) as number | null,
                        percentageSourceGroup: ((cf.percentage_source_group ??
                            cf.percentageSourceGroup ??
                            state.cogsCompound.percentageSourceGroup) as string) as never,
                    };
                }
            }

            const ae = ws.advanced_economics_state as Record<string, unknown> | undefined;
            if (ae && Array.isArray(ae.scenario_columns) && ae.scenario_columns.length > 0) {
                const cols = ae.scenario_columns as Array<Record<string, unknown>>;
                state.aeScenarios = cols.map((s, i) => {
                    const base = state.aeScenarios[i] ?? state.aeScenarios[0] ?? baseScenario();
                    const num = (snake: unknown, camel: unknown, fallback: number) =>
                        typeof (snake ?? camel) === 'number' ? ((snake ?? camel) as number) : fallback;
                    return {
                        ...base,
                        slotId: ((s.slot_id ?? s.slotId) as string) || base.slotId,
                        templateId: ((s.template_id ?? s.templateId) as TemplateId) || base.templateId,
                        tacosPercent: num(s.tacos_percent, s.tacosPercent, base.tacosPercent ?? 0),
                        tacosCustom:
                            typeof (s.tacos_custom ?? s.tacosCustom) === 'boolean'
                                ? ((s.tacos_custom ?? s.tacosCustom) as boolean)
                                : base.tacosCustom,
                        sellPrice: num(s.sell_price, s.sellPrice, base.sellPrice),
                        cogs: typeof s.cogs === 'number' ? (s.cogs as number) : base.cogs,
                        unitsSoldOnThisScenario: num(
                            s.units_sold_on_this_scenario,
                            s.unitsSoldOnThisScenario,
                            base.unitsSoldOnThisScenario ?? 0
                        ),
                        compareAgainstLeft:
                            typeof (s.compare_against_left ?? s.compareAgainstLeft) === 'boolean'
                                ? ((s.compare_against_left ?? s.compareAgainstLeft) as boolean)
                                : base.compareAgainstLeft,
                    };
                });
            }

            const tabMap: Record<string, CalculatorTab> = {
                profitability_check: 0,
                cogs_builder: 1,
                advanced_economics: 2,
                launch_budget: 3,
            };
            if (
                restoreActiveTab &&
                typeof ws.active_tab === 'string' &&
                tabMap[ws.active_tab] !== undefined
            ) {
                state.activeTab = tabMap[ws.active_tab];
            }
        },
        // Start a fresh calculation (sauron "New Calculation")
        resetToEmpty() {
            return { ...initialState, appStatus: 'empty' as AppStatus };
        },
    },
});

export const calculatorActions = calculatorSlice.actions;

// ── Selectors ─────────────────────────────────────────────────────────────────
export const selectCalculator = (state: RootState) => state.calculator;

export default calculatorSlice.reducer;
